import { Head, Link } from "@inertiajs/react"
import { useState } from "react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import type { SeoMeta } from "@/types"
import { getOfficesBySlugs, getLocationBySlug, type Office } from "@/data/directory/registry"

/* ------------------------------------------------------------------ */
/* Office logo                                                          */
/* ------------------------------------------------------------------ */

function getInitials(name: string) {
  return name
    .split(/[\s&]+/)
    .filter((w) => w.length > 0 && w[0] === w[0].toUpperCase())
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
}

function getFaviconUrl(website: string, size = 64) {
  try {
    const { hostname } = new URL(website)
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=${size}`
  } catch {
    return null
  }
}

function OfficeLogo({
  office,
  size = "md",
}: {
  office: Office
  size?: "sm" | "md" | "lg"
}) {
  const [imgFailed, setImgFailed] = useState(false)
  const faviconUrl = getFaviconUrl(office.website)
  const initials = getInitials(office.name)

  const sizeClasses = {
    sm: "size-8 text-xs",
    md: "size-10 text-sm",
    lg: "size-14 text-lg",
  }[size]

  if (faviconUrl && !imgFailed) {
    return (
      <div className={`${sizeClasses} flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-stone bg-white`}>
        <OptimizedImage
          src={faviconUrl}
          alt={`${office.name} logo`}
          className="size-full object-contain p-1"
          onError={() => setImgFailed(true)}
        />
      </div>
    )
  }

  return (
    <div
      className={`${sizeClasses} flex shrink-0 items-center justify-center rounded-lg border border-stone bg-charcoal font-display font-bold text-cream`}
    >
      {initials}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Icons                                                                */
/* ------------------------------------------------------------------ */

function IconStar({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={`size-4 ${filled ? "text-amber-400" : "text-stone"}`}>
      <path
        fillRule="evenodd"
        d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconCheck() {
  return (
    <svg viewBox="0 0 20 20" fill="#C41E2A" className="size-5">
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconX() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5 text-warm-grey/50">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  )
}

function IconExternalLink() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5">
      <path
        fillRule="evenodd"
        d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Zm7.49-2.25a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0V4.81l-7.22 7.22a.75.75 0 1 1-1.06-1.06l7.22-7.22H11.74a.75.75 0 0 1-.75-.75Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Comparison row helpers                                               */
/* ------------------------------------------------------------------ */

function ComparisonRow({
  label,
  values,
  highlight = false,
}: {
  label: string
  values: React.ReactNode[]
  highlight?: boolean
}) {
  return (
    <tr className={highlight ? "bg-cream/40" : ""}>
      <td className="py-3 pr-4 text-sm font-medium text-charcoal align-top">{label}</td>
      {values.map((v, i) => (
        <td key={i} className="px-4 py-3 text-sm text-slate text-center align-top">
          {v}
        </td>
      ))}
    </tr>
  )
}

function BooleanCell({ value }: { value: boolean }) {
  return value ? <IconCheck /> : <IconX />
}

/* ------------------------------------------------------------------ */
/* Empty state                                                          */
/* ------------------------------------------------------------------ */

function EmptyState({ location }: { location: string }) {
  return (
    <PublicLayout>
      <section className="px-6 py-32 text-center">
        <h1 className="font-display text-4xl font-bold text-charcoal">
          Select offices to compare<span className="text-red">.</span>
        </h1>
        <p className="mx-auto mt-4 max-w-md text-slate">
          Go back to the directory and tick at least two offices to compare them side by side.
        </p>
        <Link
          href={`/directory/${location}`}
          className="mt-6 inline-flex items-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red"
        >
          Back to directory
        </Link>
      </section>
    </PublicLayout>
  )
}

/* ------------------------------------------------------------------ */
/* Mobile comparison cards                                              */
/* ------------------------------------------------------------------ */

function MobileComparisonCards({ offices, location }: { offices: Office[]; location: string }) {
  return (
    <div className="space-y-8 md:hidden">
      {offices.map((office) => (
        <div key={office.slug} className="rounded-xl border border-stone overflow-hidden">
          {/* Header */}
          <div className="bg-cream px-5 py-4">
            <div className="flex items-center gap-3">
              <OfficeLogo office={office} size="md" />
              <div>
                <Link
                  href={`/directory/${location}/${office.slug}`}
                  className="font-display text-lg font-bold text-charcoal hover:text-red transition-colors"
                >
                  {office.name}
                </Link>
                <p className="text-sm text-slate">{office.branch}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <IconStar key={s} filled={s <= Math.round(office.rating)} />
                ))}
              </div>
              <span className="text-sm font-medium text-charcoal">{office.rating}</span>
              <span className="text-xs text-warm-grey">({office.reviewCount})</span>
            </div>
          </div>

          {/* Details */}
          <div className="divide-y divide-stone/50 px-5">
            {[
              { label: "Address", value: `${office.address}, ${office.postcode}` },
              { label: "Phone", value: office.phone },
              { label: "Established", value: String(office.established) },
              { label: "Active listings", value: `~${office.propertyCount}` },
              { label: "Average price", value: office.avgPrice },
              { label: "Specialisms", value: office.specialisms.join(", ") },
              { label: "Areas", value: office.areas.join(", ") },
              { label: "Hours", value: office.hours.map((h) => `${h.days}: ${h.time}`).join(" | ") },
            ].map((row) => (
              <div key={row.label} className="flex justify-between gap-3 py-3">
                <span className="text-sm font-medium text-charcoal">{row.label}</span>
                <span className="text-right text-sm text-slate">{row.value}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 border-t border-stone px-5 py-4">
            <Link
              href={`/directory/${location}/${office.slug}`}
              className="text-sm font-medium text-red hover:text-deep-red transition-colors"
            >
              View profile
            </Link>
            <a
              href={office.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-slate hover:text-charcoal transition-colors"
            >
              Website <IconExternalLink />
            </a>
          </div>
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

const ALL_SPECIALISMS = [
  "Residential Sales",
  "Lettings",
  "Property Management",
  "New Homes",
  "Commercial",
  "Land & Development",
  "Auctions",
  "Relocation",
] as const

export default function DirectoryCompare({
  meta,
  location,
  officeSlugs,
}: {
  meta: SeoMeta
  location: string
  officeSlugs: string[]
}) {
  const offices = getOfficesBySlugs(location, officeSlugs)
  const locationData = getLocationBySlug(location)
  const cityName = locationData?.name ?? location

  if (offices.length < 2) return <EmptyState location={location} />

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Breadcrumb */}
      <div className="border-b border-stone bg-cream/50 px-6 py-3">
        <div className="mx-auto max-w-6xl">
          <nav className="flex items-center gap-2 text-sm text-warm-grey">
            <Link href="/directory" className="transition-colors hover:text-charcoal">
              Directory
            </Link>
            <span>/</span>
            <Link href={`/directory/${location}`} className="transition-colors hover:text-charcoal">
              {cityName}
            </Link>
            <span>/</span>
            <span className="text-charcoal">Compare ({offices.length})</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="px-6 py-16 md:py-20">
        <div className="mx-auto max-w-6xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Compare</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal md:text-5xl">
            Side by side<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate">
            Comparing {offices.map((o) => o.name).join(", ")} - see which estate agent
            is the best fit for your needs.
          </p>
        </div>
      </section>

      {/* Desktop comparison table */}
      <section className="border-t border-stone bg-white px-6 py-12">
        <div className="mx-auto max-w-6xl">
          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-stone">
                  <th className="py-4 pr-4 text-left text-xs font-medium uppercase tracking-[0.15em] text-warm-grey w-40" />
                  {offices.map((o) => (
                    <th key={o.slug} className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <OfficeLogo office={o} size="md" />
                        <Link
                          href={`/directory/${location}/${o.slug}`}
                          className="font-display text-base font-bold text-charcoal hover:text-red transition-colors"
                        >
                          {o.name}
                        </Link>
                        <p className="-mt-1 text-xs text-warm-grey">{o.branch}</p>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone/50">
                <ComparisonRow
                  label="Rating"
                  highlight
                  values={offices.map((o) => (
                    <div key={o.slug} className="flex items-center justify-center gap-1.5">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <IconStar key={s} filled={s <= Math.round(o.rating)} />
                        ))}
                      </div>
                      <span className="font-medium text-charcoal">{o.rating}</span>
                    </div>
                  ))}
                />
                <ComparisonRow
                  label="Reviews"
                  values={offices.map((o) => <span key={o.slug}>{o.reviewCount}</span>)}
                />
                <ComparisonRow
                  label="Established"
                  highlight
                  values={offices.map((o) => <span key={o.slug}>{o.established}</span>)}
                />
                <ComparisonRow
                  label="Active listings"
                  values={offices.map((o) => <span key={o.slug}>~{o.propertyCount}</span>)}
                />
                <ComparisonRow
                  label="Average price"
                  highlight
                  values={offices.map((o) => <span key={o.slug} className="font-medium text-charcoal">{o.avgPrice}</span>)}
                />
                <ComparisonRow
                  label="Address"
                  values={offices.map((o) => (
                    <span key={o.slug} className="text-xs">
                      {o.address}<br />{o.postcode}
                    </span>
                  ))}
                />
                <ComparisonRow
                  label="Phone"
                  highlight
                  values={offices.map((o) => (
                    <a key={o.slug} href={`tel:${o.phone.replace(/\s/g, "")}`} className="text-red hover:text-deep-red">
                      {o.phone}
                    </a>
                  ))}
                />

                {/* Specialisms */}
                {ALL_SPECIALISMS.map((spec, i) => (
                  <ComparisonRow
                    key={spec}
                    label={i === 0 ? "Specialisms" : ""}
                    highlight={i % 2 === 0}
                    values={offices.map((o) => (
                      <div key={o.slug} className="flex items-center justify-center gap-2">
                        <BooleanCell value={o.specialisms.includes(spec)} />
                        <span className="text-xs">{spec}</span>
                      </div>
                    ))}
                  />
                ))}

                <ComparisonRow
                  label="Areas covered"
                  highlight
                  values={offices.map((o) => (
                    <div key={o.slug} className="flex flex-wrap justify-center gap-1">
                      {o.areas.map((a) => (
                        <span key={a} className="rounded-full bg-stone/50 px-2 py-0.5 text-[10px]">
                          {a}
                        </span>
                      ))}
                    </div>
                  ))}
                />

                <ComparisonRow
                  label="Opening hours"
                  values={offices.map((o) => (
                    <div key={o.slug} className="space-y-0.5 text-xs">
                      {o.hours.map((h) => (
                        <div key={h.days}>
                          <span className="font-medium">{h.days}:</span> {h.time}
                        </div>
                      ))}
                    </div>
                  ))}
                />

                {/* Action row */}
                <tr>
                  <td className="py-5" />
                  {offices.map((o) => (
                    <td key={o.slug} className="px-4 py-5 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Link
                          href={`/directory/${location}/${o.slug}`}
                          className="inline-flex items-center rounded-lg bg-red px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-deep-red"
                        >
                          View profile
                        </Link>
                        <a
                          href={o.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-slate hover:text-charcoal transition-colors"
                        >
                          Website <IconExternalLink />
                        </a>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <MobileComparisonCards offices={offices} location={location} />
        </div>
      </section>

      {/* Back to directory */}
      <section className="border-t border-stone px-6 py-12 text-center">
        <Link
          href={`/directory/${location}`}
          className="inline-flex items-center gap-2 text-sm font-medium text-red transition-colors hover:text-deep-red"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
            <path
              fillRule="evenodd"
              d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z"
              clipRule="evenodd"
            />
          </svg>
          Back to directory
        </Link>
      </section>
    </PublicLayout>
  )
}
